import { createElement } from 'react';
import App from 'next/app';
import { CookiesProvider, Cookies } from 'react-cookie';
import { DeviceProvider } from '@atc/react-device';
import { getDuckEgg, wrapperInitializer } from '../redux/store.js';
import { getDeviceContext } from '../device/getDeviceContext.js';
import { deviceDuck, setInitialReduxDeviceState, ReduxDeviceClient } from '../device/redux-device.js';
import { PageEventProvider } from '../use-page-event/use-page-event.js';
import { enhanceNextCtx } from '../ctx-middleware/enhanceNextCtx.js';

function composeProviders(items, children) {
  return items.reduceRight((acc, [Component, props]) => /*#__PURE__*/createElement(Component, props, acc), children);
}
const withBonnetApp = (AppComponent, options = {}) => {
  const {
    reducers: appDucks = [],
    ...rest
  } = options;
  const duckEggs = [
  // handles responsive device state
  deviceDuck,
  // Duck/object reducers supplied by the app component
  ...appDucks].map(getDuckEgg);
  const wrapper = wrapperInitializer.getAppWrapper(duckEggs, {
    ...rest
  });
  const AppComponentWithBonnetProviders = props => {
    const {
      bonnetAppProps,
      ...restAppProps
    } = props;
    const providers = [[PageEventProvider, {}], [DeviceProvider, bonnetAppProps.deviceContext], [ReduxDeviceClient, {}]];

    // Server side cookie provider support
    if (typeof window === 'undefined') {
      providers.push([CookiesProvider, {
        cookies: new Cookies(bonnetAppProps.cookies)
      }]);
    } else {
      providers.push([CookiesProvider, undefined]);
    }
    return composeProviders(providers, /*#__PURE__*/createElement(AppComponent, restAppProps));
  };
  const BonnetAppComponent = wrapper.wrapApp(AppComponentWithBonnetProviders);
  BonnetAppComponent.getInitialProps = wrapper.wrapGetInitialProps(store => async appContext => {
    await enhanceNextCtx(appContext.ctx, store);

    // add adaptive device data
    const deviceContext = getDeviceContext(appContext.ctx.req?.device);
    setInitialReduxDeviceState(store, deviceContext);

    // run any potential custom defined getInitialProps method
    // This MUST be called before the original nextJS defined `App.getInitialProps`
    // Not doing so will not allow custom app dispatched redux state to be
    // available to the subsequent page
    let appComponentProps = {};
    if (AppComponent.getInitialProps) {
      appComponentProps = await AppComponent.getInitialProps(appContext);
    }

    // Call the original nextjs App getInitialProps method
    // https://nextjs.org/docs/advanced-features/custom-app#caveats
    const appProps = await App.getInitialProps(appContext);
    return {
      ...appProps,
      ...appComponentProps,
      bonnetAppProps: {
        cookies: appContext.ctx.req?.cookies,
        deviceContext: {
          ...deviceContext,
          // add any react-device configuration supplied from bonnet config
          ...appContext.ctx.req?.reactDevice
        }
      }
    };
  });
  return {
    BonnetAppComponent
  };
};

export { withBonnetApp };
